import React, { useState, useRef, useEffect } from "react";

interface ScrollingTextProps {
  text: string;
  className?: string;
}

const ScrollingText = ({ text, className = "" }: ScrollingTextProps) => {
  const [shouldScroll, setShouldScroll] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isLongPressed, setIsLongPressed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const longPressTimeoutRef = useRef<NodeJS.Timeout>();
  const isTouchDevice = useRef(false);

  useEffect(() => {
    isTouchDevice.current = "ontouchstart" in window;
  }, []);

  useEffect(() => {
    const checkIfShouldScroll = () => {
      if (containerRef.current && textRef.current) {
        const shouldScrollText =
          textRef.current.scrollWidth > containerRef.current.clientWidth;
        setShouldScroll(shouldScrollText);
      }
    };

    checkIfShouldScroll();
    window.addEventListener("resize", checkIfShouldScroll);
    return () => window.removeEventListener("resize", checkIfShouldScroll);
  }, [text]);

  const handleTouchStart = () => {
    if (!isTouchDevice.current || !shouldScroll) return;

    longPressTimeoutRef.current = setTimeout(() => {
      setIsLongPressed(true);
    }, 500);
  };

  const handleTouchEnd = () => {
    if (!isTouchDevice.current) return;

    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
    }
    setIsLongPressed(false);
  };

  const isScrolling = (isHovering || isLongPressed) && shouldScroll;

  // Calculate scroll amount - only enough to reveal the end of text
  const getScrollAmount = () => {
    if (!containerRef.current || !textRef.current) return 0;
    const containerWidth = containerRef.current.clientWidth;
    const textWidth = textRef.current.scrollWidth;
    return Math.min(textWidth - containerWidth, textWidth);
  };

  // Calculate duration based on a fixed speed (pixels per second)
  const getScrollDuration = () => {
    const scrollAmount = getScrollAmount();
    const PIXELS_PER_SECOND = 30; // Adjust this value to change scroll speed
    return (scrollAmount / PIXELS_PER_SECOND) * 1000; // Convert to milliseconds
  };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => !isTouchDevice.current && setIsHovering(true)}
      onMouseLeave={() => !isTouchDevice.current && setIsHovering(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div
        ref={textRef}
        style={{
          transform: isScrolling
            ? `translateX(-${getScrollAmount()}px)`
            : "translateX(0)",
          transition: isScrolling
            ? `transform ${getScrollDuration()}ms linear`
            : "transform 0s",
        }}
        className="whitespace-nowrap"
      >
        {text}
      </div>
    </div>
  );
};

export default ScrollingText;
